import { eachDayOfInterval, format }                                                       from 'date-fns';
import { DEFAULT_DATE_FORMAT }                                                             from '@/variables';
import providers                                                                           from '@/providers';
import { getViewStorePath }                                                                from '@/store/store.helpers';
import { Layout }                                                                          from '@/components/chart/chart.types';
import { chartWithUuid, Elements, ElementType }                                            from '@/helpers/variables';
import { BuilderConfiguration, BuilderConfigurationElement, BuilderElement, BuilderValue } from '@/views/x-axis-report/report-edit/types/types';
import { FILTER_TYPE }                                                                     from '@/components/filter-panel/filter-panel.types';
import { XAxisReportPanelGroups }                                                          from '@/views/x-axis-report/report-edit/helpers/x-axis-report.variables';

export const getXAxisViewMode = (): string => providers?.router?.currentRoute.meta.viewInMode || '';
export const getXAxisStorePath = (): string => getViewStorePath(getXAxisViewMode());
export const getLayoutUuidPath = (layout: Layout | undefined): string => (layout ? `${chartWithUuid(layout.chartUuid)}/` : '');
export const getXAxisStorePathWithChartUuid = (layout?: Layout): string => (layout ? `${getXAxisStorePath()}/${getLayoutUuidPath(layout)}` : `${getXAxisStorePath()}/`);
export const getLayoutUuid = (layout: Layout | undefined): string | undefined => (layout ? `${chartWithUuid(layout.chartUuid)}` : undefined);

export const getProperStartAndEndValues = (startValue: string, endValue: string): { start: string; end: string } => {
  let start: string = startValue;
  let end: string = endValue;

  if (+startValue > +endValue) {
    const newEnd: string = endValue;
    end = startValue;
    start = newEnd;
  }

  return {
    start,
    end,
  };
};

export const getDateRangeEachDay: (firstDate: string, lastDate: string) => string[] = (firstDate: string, lastDate: string) => {
  const { start, end } = getProperStartAndEndValues(firstDate, lastDate);

  return eachDayOfInterval({ start: new Date(start), end: new Date(end) })
    .map((date: Date) => format(date, DEFAULT_DATE_FORMAT));
};

export const generateLeadTimeRange = (from: string, to: string): string[] => {
  const { start, end } = getProperStartAndEndValues(from, to);
  const result: string[] = [];

  if (!!start && !!end) {
    for (let i = +start; i <= +end; i += 1) {
      result.push((-Math.abs(i)).toString());
    }
  }

  return result.reverse();
};

export const duplicateArr = (arr: string[], times: number): string[] => Array(times)
  .fill([ ...arr ])
  .reduce((a: string[], b: string[]) => a.concat(b));

export const getFilterComponentType = (filter: BuilderConfiguration): FILTER_TYPE => {
  switch (filter.type) {
  case ElementType.RANGE:
    return FILTER_TYPE.MULTI_DATE_RANGE;
  case ElementType.DAY:
    return FILTER_TYPE.DATE_RANGE;
  case ElementType.VALUE:
  default:
    return FILTER_TYPE.CHECKBOX_WITH_OPTIONS;
  }
};

export const sortByOrder = <T extends { order: number | null }>(prevElement: T, currentElement: T, ascending = true): number => {
  if (prevElement.order != null && currentElement.order != null && prevElement.order > currentElement.order) {
    return !ascending ? -1 : 1;
  }
  return prevElement.order == null || !ascending ? 1 : -1;
};

export const reOrderData = <T>(el: T, order: number): T => ({
  ...el,
  order,
});

export const runCallbackOnAllElements = (callback: (key: XAxisReportPanelGroups) => void): void => {
  Object.values(XAxisReportPanelGroups)
    .forEach((key: XAxisReportPanelGroups) => {
      if (key !== XAxisReportPanelGroups.FUNCTIONS) {
        callback(key);
      }
    });
};

export const isElementNotTypeValue = (element: BuilderElement): boolean => (
  element?.type === ElementType.DATE_RANGE
  || element?.type === ElementType.DAY
  || element?.columnName === Elements.LEAD_TIME);

export const isElementTypeDate = (element: BuilderElement | BuilderConfigurationElement): boolean => (
  element.type === ElementType.DATE_RANGE || element.type === ElementType.DAY || element.type === ElementType.RANGE
);

export const isElementLeadTime = <T extends { columnName?: string | undefined; name?: string | undefined }>(element?: T): boolean => (
  element?.columnName === Elements.LEAD_TIME || element?.name === Elements.LEAD_TIME
);

export const isGroupFilter = (groupName: XAxisReportPanelGroups): boolean => (
  groupName === XAxisReportPanelGroups.FILTERS
);
export const generateDefaultValues = (): { value: BuilderValue } => ({
  value: {
    from: '',
    to: '',
  },
});
